import { supabase } from '../lib/supabase';
import { isDemoMode, getDemoOrgId, getAuthOrgId } from '../utils/demoHelper';

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  if (typeof obj !== 'object') return obj;

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  if (typeof obj !== 'object') return obj;

  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

// Check if error is schema-related
function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

export const calendarService = {
  // Get all calendar events
  async getAll() {
    try {
      let query = supabase
        ?.from('calendar_events')
        ?.select('*')
        ?.order('start_date', { ascending: true });

      // Demo mode: use stored demo org_id directly
      if (isDemoMode()) {
        const demoOrgId = getDemoOrgId();
        if (demoOrgId) {
          query = query?.eq('organization_id', demoOrgId);
        }
      } else {
        // Use cached org_id from AuthContext (never stale after token refresh)
        const orgId = getAuthOrgId();
        if (orgId) {
          query = query?.eq('organization_id', orgId);
        }
      }

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching calendar events:', error);
      throw error;
    }
  },

  // Get events by date range
  async getByDateRange(startDate, endDate) {
    try {
      let query = supabase
        ?.from('calendar_events')
        ?.select('*')
        ?.gte('start_date', startDate)
        ?.lte('end_date', endDate)
        ?.order('start_date', { ascending: true });

      // Demo mode: use stored demo org_id directly
      if (isDemoMode()) {
        const demoOrgId = getDemoOrgId();
        if (demoOrgId) {
          query = query?.eq('organization_id', demoOrgId);
        }
      } else {
        // Use cached org_id from AuthContext (never stale after token refresh)
        const orgId = getAuthOrgId();
        if (orgId) {
          query = query?.eq('organization_id', orgId);
        }
      }

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching calendar events by date range:', error);
      throw error;
    }
  },

  // Get events by type
  async getByType(eventType) {
    try {
      const { data, error } = await supabase
        ?.from('calendar_events')
        ?.select('*')
        ?.eq('event_type', eventType)
        ?.order('start_date', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching calendar events by type:', error);
      throw error;
    }
  },

  // Get event by ID
  async getById(id) {
    try {
      const { data, error } = await supabase
        ?.from('calendar_events')
        ?.select('*')
        ?.eq('id', id)
        ?.maybeSingle();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return null;
        }
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Error fetching calendar event:', error);
      throw error;
    }
  },

  // Get event with related details (project, contractor, team member)
  async getByIdWithRelations(id) {
    try {
      const { data, error } = await supabase
        ?.from('calendar_events')
        ?.select('*, projects(*, customers(name)), contractors(*), team_member:user_profiles!calendar_events_team_member_id_fkey(*), created_by_user:user_profiles!calendar_events_created_by_fkey(*)')
        ?.eq('id', id)
        ?.maybeSingle();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return null;
        }
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Error fetching calendar event with relations:', error);
      throw error;
    }
  },

  // Create new calendar event
  async create(eventData) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      if (!session?.user) throw new Error('User not authenticated');
      const user = session?.user;

      // Use cached org_id from AuthContext
      const organizationId = getAuthOrgId();
      if (!organizationId) throw new Error('Unable to determine user organization');

      const snakeData = toSnakeCase({
        ...eventData,
        createdBy: user?.id,
        organizationId
      });

      const { data, error } = await supabase
        ?.from('calendar_events')
        ?.insert(snakeData)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          throw new Error(error.message);
        }
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Error creating calendar event:', error);
      throw error;
    }
  },

  // Update calendar event
  async update(id, eventData) {
    try {
      const snakeData = toSnakeCase(eventData);

      const { data, error } = await supabase
        ?.from('calendar_events')?.update(snakeData)?.eq('id', id)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          throw new Error(error.message);
        }
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Error updating calendar event:', error);
      throw error;
    }
  },

  // Delete calendar event
  async delete(id) {
    try {
      const { error } = await supabase
        ?.from('calendar_events')?.delete()?.eq('id', id);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          throw new Error(error.message);
        }
      }

      return true;
    } catch (error) {
      console.error('Error deleting calendar event:', error);
      throw error;
    }
  },

  // Get events for a specific project
  async getByProject(projectId) {
    try {
      const { data, error } = await supabase
        ?.from('calendar_events')?.select('*')?.eq('project_id', projectId)?.order('start_date', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching project calendar events:', error);
      throw error;
    }
  },

  // Get events for a specific contractor
  async getByContractor(contractorId) {
    try {
      const { data, error } = await supabase
        ?.from('calendar_events')?.select('*')?.eq('contractor_id', contractorId)?.order('start_date', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching contractor calendar events:', error);
      throw error;
    }
  },

  // Get events for a specific team member
  async getByTeamMember(teamMemberId) {
    try {
      const { data, error } = await supabase
        ?.from('calendar_events')?.select('*')?.eq('team_member_id', teamMemberId)?.order('start_date', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Error fetching team member calendar events:', error);
      throw error;
    }
  },

  // Sync project milestone events when phase or due date changes
  // Creates/updates a phase milestone event and a deadline event for the project
  async syncProjectMilestones(projectId, projectData) {
    try {
      if (!projectData) return;
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return;

      // Get user's organization_id
      const { data: profile } = await supabase
        ?.from('user_profiles')?.select('organization_id')?.eq('id', user?.id)?.single();

      if (!profile?.organization_id) return;

      const orgId = profile?.organization_id;
      const { phase, customerName, address, dueDate } = projectData;

      const phaseLabels = {
        planning: 'Planning',permits: 'Permits',foundation: 'Foundation',framing: 'Framing',systems: 'Systems',finishing: 'Finishing',completed: 'Completion',completion: 'Completion'
      };

      const projectLabel = customerName || address || 'Project';

      // --- Phase milestone event ---
      if (phase) {
        const phaseLabel = phaseLabels?.[phase] || phase;
        const milestoneTitle = `${projectLabel} — ${phaseLabel} Phase`;
        const milestoneDate = new Date()?.toISOString();

        // Check if a phase milestone already exists for this project
        const { data: existingPhase } = await supabase
          ?.from('calendar_events')
          ?.select('id')
          ?.eq('project_id', projectId)
          ?.eq('event_type', 'milestone')
          ?.ilike('title', '% Phase')
          ?.not('title', 'ilike', '%Deadline%')
          ?.maybeSingle();

        if (existingPhase?.id) {
          // Update existing phase milestone
          await supabase
            ?.from('calendar_events')
            ?.update({
              title: milestoneTitle,
              description: `Project entered the ${phaseLabel} phase`,
              start_date: milestoneDate,
              end_date: milestoneDate,
              updated_at: new Date()?.toISOString()
            })
            ?.eq('id', existingPhase?.id);
        } else {
          // Create new phase milestone
          await supabase
            ?.from('calendar_events')
            ?.insert({
              title: milestoneTitle,
              description: `Project entered the ${phaseLabel} phase`,
              event_type: 'milestone',
              start_date: milestoneDate,
              end_date: milestoneDate,
              all_day: true,
              project_id: projectId,
              organization_id: orgId,
              created_by: user?.id
            });
        }
      }

      // --- Due date deadline event ---
      if (dueDate) {
        const deadlineTitle = `${projectLabel} — Project Deadline`;
        const deadlineDate = new Date(dueDate)?.toISOString();

        // Check if a deadline event already exists for this project
        const { data: existingDeadline } = await supabase
          ?.from('calendar_events')
          ?.select('id')
          ?.eq('project_id', projectId)
          ?.eq('event_type', 'milestone')
          ?.ilike('title', '%Deadline%')
          ?.maybeSingle();

        if (existingDeadline?.id) {
          // Update existing deadline
          await supabase
            ?.from('calendar_events')
            ?.update({
              title: deadlineTitle,
              description: `Project due date for ${projectLabel}`,
              start_date: deadlineDate,
              end_date: deadlineDate,
              updated_at: new Date()?.toISOString()
            })
            ?.eq('id', existingDeadline?.id);
        } else {
          // Create new deadline event
          await supabase
            ?.from('calendar_events')
            ?.insert({
              title: deadlineTitle,
              description: `Project due date for ${projectLabel}`,
              event_type: 'milestone',
              start_date: deadlineDate,
              end_date: deadlineDate,
              all_day: true,
              project_id: projectId,
              organization_id: orgId,
              created_by: user?.id
            });
        }
      }
    } catch (error) {
      // Non-blocking — log but don't throw
      console.error('Error syncing project milestones to calendar:', error);
    }
  }
};